import { useState } from 'react';
import { Download, Timer, Trash2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';

// Formata milissegundos como "mm:ss.cs" (espelha cronometro-leo/src/lib/tempo.ts).
function formatTempo(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const totalCs = Math.floor(safe / 10);
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const s = totalSec % 60;
  const m = Math.floor(totalSec / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(s)}.${pad(cs)}`;
}

export function CronometroPage() {
  const { data, isLoading, isError, error, refetch } = trpc.cronometro.listarTempos.useQuery();
  const deletarTempo = trpc.cronometro.deletarTempo.useMutation({
    onSuccess: () => refetch(),
  });
  const [tabAtivo, setTabAtivo] = useState<string | null>(null);

  const handleDeletar = (id: string, clube: string) => {
    if (window.confirm(`Remover o tempo registrado de ${clube}?`)) {
      deletarTempo.mutate({ id });
    }
  };

  if (isLoading) {
    return <div className="text-gray-500">Carregando tempos…</div>;
  }

  if (isError || !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
        Não foi possível carregar os tempos
        {error?.message ? `: ${error.message}` : '. Tente recarregar a página.'}
      </div>
    );
  }

  const concursoAtivo = data.find((c) => c.slug === tabAtivo) ?? data[0];
  const totalMarcacoes = data.reduce((acc, c) => acc + c.tempos.length, 0);

  const exportarCsv = () => {
    const linhas = [['Concurso', 'Posição', 'Clube', 'Tempo', 'Tempo (ms)', 'Data']];
    for (const c of data) {
      c.tempos.forEach((t, i) => {
        linhas.push([
          c.label,
          String(i + 1),
          t.clube,
          formatTempo(t.tempoMs),
          String(t.tempoMs),
          new Date(t.createdAt).toLocaleString('pt-BR'),
        ]);
      });
    }
    const csv =
      '﻿' +
      linhas.map((l) => l.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tempos-concursos-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Timer className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Cronômetro — Concursos Artísticos</h1>
        </div>
        <Button onClick={exportarCsv} variant="outline" disabled={totalMarcacoes === 0}>
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {totalMarcacoes === 0 ? (
        <Card>
          <CardContent>
            <p className="py-12 text-center text-gray-500">
              Nenhum tempo registrado ainda. Os tempos aparecem aqui assim que forem
              cronometrados no app.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Cards-resumo: total de clubes com tempo por concurso */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {data.map((c) => (
              <Card key={c.slug}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-2 text-base">
                    <span>{c.label}</span>
                    <span className="shrink-0 text-sm font-medium text-primary-600">
                      {c.tempos.length}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500">
                    {c.tempos.length === 0
                      ? 'sem tempos'
                      : c.tempos.length === 1
                        ? '1 clube'
                        : `${c.tempos.length} clubes`}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs por concurso */}
          <div className="flex flex-wrap gap-2 border-b border-gray-200">
            {data.map((c) => {
              const ativo = (tabAtivo ?? data[0]?.slug) === c.slug;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setTabAtivo(c.slug)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    ativo
                      ? 'border-b-2 border-primary-600 text-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {c.label} ({c.tempos.length})
                </button>
              );
            })}
          </div>

          {/* Tabela ranqueada do concurso ativo */}
          {concursoAtivo && (
            <Card>
              <CardContent className="p-0">
                {concursoAtivo.tempos.length === 0 ? (
                  <p className="py-12 text-center text-gray-500">
                    Nenhum tempo registrado neste concurso.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">#</TableHead>
                        <TableHead>Clube</TableHead>
                        <TableHead>Tempo</TableHead>
                        <TableHead>Registrado em</TableHead>
                        <TableHead className="w-16 text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {concursoAtivo.tempos.map((t, i) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-semibold text-gray-500">{i + 1}</TableCell>
                          <TableCell className="font-medium">{t.clube}</TableCell>
                          <TableCell className="font-mono text-base font-semibold text-gray-900">
                            {formatTempo(t.tempoMs)}
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {new Date(t.createdAt).toLocaleString('pt-BR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletar(t.id, t.clube)}
                              isLoading={
                                deletarTempo.isPending && deletarTempo.variables?.id === t.id
                              }
                              title="Remover tempo"
                              className="text-red-600 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
